import { ChevronDownIcon, ChevronUpIcon, DocumentDownloadIcon } from '@heroicons/react/outline';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { db, generateSerialLetter, useAuth } from '../../firebase';
import { formatByType } from '../../utils/numberFormat';
import IdDropdown from '../../components/IdDropdown';
import NameDropdown from '../../components/NameDropdown';

function CreateLocalFile({ selectedCompany }) {
  const currentUser = useAuth();

  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState();
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState();

  const [yearsVisible, setYearsVisible] = useState(false);
  const [subsidiariesVisible, setSubsidiariesVisible] = useState(false);

  const [templatesVisible, setTemplatesVisible] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState();

  const [advancedVisible, setAdvancedVisible] = useState(true);
  const [amountDecimals, setAmountDecimals] = useState(2);
  const [percentDecimals, setPercentDecimals] = useState(2);
  const [decimalDelimiter, setDecimalDelimiter] = useState('.');

  /// Loading available Templates and Document Types ///

  useEffect(() => {
    async function fetchYearsTemplates() {
      if (selectedCompany?.id) {
        const newYears = await getDocs(
          query(collection(db, 'user_data', selectedCompany?.id, 'years'))
        );
        setYears(newYears.docs);
      }
    }

    fetchYearsTemplates();
  }, [selectedCompany]);

  /// Document Type, Document and Template Selection ///

  async function selectYear(year) {
    setSelectedYear(year);
    setYearsVisible(false);

    try {
      // Get current user's access groups
      const userDocRef = doc(db, 'user_data', selectedCompany.id, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      const currentUserAccessGroups = userDoc.data()?.accessGroups || [];
      console.log(`Current User Access Groups: ${currentUserAccessGroups}`);

      // Fetch all subsidiaries for the selected year
      const subsidiariesRef = collection(
        db,
        'user_data',
        selectedCompany.id,
        'years',
        year.id,
        'subsidaries'
      );
      const newSubsidiariesSnapshot = await getDocs(subsidiariesRef);

      // Filter subsidiaries based on access groups
      const filteredSubsidiaries = newSubsidiariesSnapshot.docs.filter((doc) => {
        const data = doc.data();
        return (
          !data.accessGroups ||
          data.accessGroups.length === 0 ||
          data.accessGroups.some((group) => currentUserAccessGroups.includes(group))
        );
      });

      setSubsidiaries(filteredSubsidiaries);
    } catch (error) {
      console.error('Error fetching subsidiaries with access group filtering: ', error);
    }

    setSubsidiariesVisible(true);
  }

  async function selectSubsidiary(sub) {
    setSelectedTemplate();

    setSelectedSubsidiary(sub);
    setSubsidiariesVisible(false);

    const q = query(
      collection(db, 'user_data', selectedCompany?.id, 'templates'),
      where('relatedSubsidaries', 'array-contains', sub.id)
    );
    const newTemplates = await getDocs(q);
    setTemplates(newTemplates.docs);

    setTemplatesVisible(true);
  }

  async function selectTemplate(template) {
    setSelectedTemplate(template);
    setTemplatesVisible(false);
  }

  /// Generate Local File ///

  async function createLocalFile() {
    const localFileData = {};

    const formFrame = await getDoc(
      doc(db, 'user_data', selectedCompany.id, 'years', selectedYear.id, 'forms', 'standard_form')
    );

    const subsidiaryData = selectedSubsidiary.data();

    // Extract 4-digit year from year ID (e.g., "LF-2024" -> "2024")
    const yearMatch = selectedYear.id.match(/\d{4}/);
    const yearValue = yearMatch ? yearMatch[0] : selectedYear.id;

    localFileData['FY'] = yearValue;
    if (/^\d+$/.test(yearValue)) {
      localFileData['FY-3'] = parseInt(yearValue) - 3;
      localFileData['FY-2'] = parseInt(yearValue) - 2;
      localFileData['FY-1'] = parseInt(yearValue) - 1;
    }

    localFileData['Status'] = 'Final';

    for (const category of formFrame
      .data()
      .categories.filter((category) => category.type === 'information')) {
      for (const field of category.fields) {
        const matchFields = subsidiaryData.fieldValues.filter(
          (fieldValue) => fieldValue.id === field.id
        );
        if (matchFields.length === 0) {
          localFileData[field.topic] = '';
          if (localFileData['Status'] !== 'Draft') {
            localFileData['Status'] = 'Draft';
          }
        } else {
          localFileData[field.topic] = matchFields[0].answer;

          if (matchFields[0].done === false) {
            if (localFileData['Status'] !== 'Draft') {
              localFileData['Status'] = 'Draft';
            }
          }
        }
      }
    }

    for (const category of formFrame
      .data()
      .categories.filter(
        (category) =>
          category.type === 'standard-transactions' ||
          category.type === 'numbers' ||
          category.type === 'individual-transactions'
      )) {
      for (const field of category.fields) {
        const matchFields = subsidiaryData.fieldValues.filter(
          (fieldValue) => fieldValue.id === field.id
        );
        if (matchFields.length === 0) {
          localFileData[field.topic] = '';
          if (localFileData['Status'] !== 'Draft') {
            localFileData['Status'] = 'Draft';
          }
        } else {
          const transactionAmount = matchFields[0]?.transaction_amount ?? 0;
          const formatted = formatByType(transactionAmount, field, {
            amount: { decimals: amountDecimals, decimalDelimiter },
            percent: { decimals: percentDecimals, decimalDelimiter, appendSymbol: false },
          });
          localFileData[field.topic] = formatted;

          if (matchFields[0].done === false) {
            if (localFileData['Status'] !== 'Draft') {
              localFileData['Status'] = 'Draft';
            }
          }
        }
      }
    }

    await generateSerialLetter(
      selectedTemplate,
      localFileData,
      selectedCompany.id,
      selectedYear.id,
      selectedSubsidiary.id
    );
  }

  /// HTML Component ///

  return (
    <div className="component-container w-[90%] lg:w-[70%] xl:w-[60%]">
      <div className="component-icon-container bg-yellow-400">
        <DocumentDownloadIcon className="w-8 h-8" />
      </div>
      <p className="text-2xl p-1 m-1">Create Local File</p>
      <div className="w-1/1 flex mt-4 px-1 gap-x-1">
        <IdDropdown
          width={'35%'}
          color={'yellow'}
          options={years}
          selectedOption={selectedYear}
          dropdownVisible={yearsVisible}
          setDropdownVisible={setYearsVisible}
          selectFunction={selectYear}
          placeholder={'Document Type'}
        />

        <IdDropdown
          width={'30%'}
          color={'yellow'}
          options={subsidiaries}
          selectedOption={selectedSubsidiary}
          dropdownVisible={subsidiariesVisible}
          setDropdownVisible={setSubsidiariesVisible}
          selectFunction={selectSubsidiary}
          placeholder={'Document'}
        />

        <NameDropdown
          width={'35%'}
          color={'yellow'}
          options={templates}
          selectedOption={selectedTemplate}
          dropdownVisible={templatesVisible}
          setDropdownVisible={setTemplatesVisible}
          selectFunction={selectTemplate}
          placeholder={'Template'}
        />
      </div>

      {/* Advanced Settings */}
      <div className="w-[100%] px-1 mt-3">
        <button
          className="w-[100%] flex items-center justify-between px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
          onClick={() => setAdvancedVisible(!advancedVisible)}
        >
          <span className="text-sm font-medium">Advanced Settings</span>
          {advancedVisible ? (
            <ChevronUpIcon className="w-5 h-5" />
          ) : (
            <ChevronDownIcon className="w-5 h-5" />
          )}
        </button>

        {advancedVisible && (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-6 flex-wrap">
              <label className="flex items-center">
                <span className="text-sm mr-3">Amount Decimals:</span>
                <input
                  type="number"
                  min="0"
                  max="2"
                  value={amountDecimals}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    const next = Number.isFinite(raw) ? Math.floor(raw) : 0;
                    const clamped = Math.max(0, Math.min(2, next));
                    setAmountDecimals(clamped);
                  }}
                  className="w-20 px-2 py-1 border border-gray-300 rounded"
                />
              </label>
              <label className="flex items-center">
                <span className="text-sm mr-3">Percent Decimals:</span>
                <input
                  type="number"
                  min="0"
                  max="4"
                  value={percentDecimals}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    const next = Number.isFinite(raw) ? Math.floor(raw) : 0;
                    const clamped = Math.max(0, Math.min(4, next));
                    setPercentDecimals(clamped);
                  }}
                  className="w-20 px-2 py-1 border border-gray-300 rounded"
                />
              </label>
            </div>
            <label className="flex items-center mt-3">
              <span className="text-sm mr-3">Decimal Delimiter:</span>
              <select
                value={decimalDelimiter}
                onChange={(e) => setDecimalDelimiter(e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded"
              >
                <option value=".">. (period)</option>
                <option value=",">, (comma)</option>
              </select>
            </label>
          </div>
        )}
      </div>

      <div className="w-[100%] px-1 mt-3">
        <button
          className="w-[100%] text-center rounded-lg bg-yellow-400 text-white p-2 transition-all hover:shadow-md"
          onClick={createLocalFile}
        >
          Download
        </button>
      </div>
    </div>
  );
}

export default CreateLocalFile;
